import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BrandingContext = createContext({
  branding: null,
  loading: true,
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(null);
  const [isMaster, setIsMaster] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [isInvalidDomain, setIsInvalidDomain] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const hostname = window.location.host;
        const apiBaseUrl =
          process.env.REACT_APP_API_URL || "http://localhost:5001";

        // Helper: resolve image URLs - prepend API base URL for backend-stored paths
        const resolveImageUrl = (url) => {
          if (!url) return url;
          if (url.startsWith("http://") || url.startsWith("https://")) return url;
          if (url.startsWith("/uploads/")) return `${apiBaseUrl}${url}`;
          if (url.startsWith("/assets/")) return `${apiBaseUrl}${url}`;
          if (url.startsWith("/favicon.ico")) return `${apiBaseUrl}${url}`;
          return url;
        };

        // Fetch branding from the public config endpoint
        const response = await axios.get(`${apiBaseUrl}/white-label/config`, {
          params: { domain: hostname },
        });

        if (response.data?.data?.branding) {
          const config = response.data.data.branding;
          
          if (response.data.data._id) {
            localStorage.setItem("tenantId", response.data.data._id);
          }

          // Resolve image URLs to use dynamic BASE_URL
          config.standardLogoUrl = resolveImageUrl(config.standardLogoUrl);
          config.whiteLogoUrl = resolveImageUrl(config.whiteLogoUrl);
          config.faviconUrl = resolveImageUrl(config.faviconUrl);

          setBranding(config);
          if (response.data.data.isMaster !== undefined) {
            setIsMaster(response.data.data.isMaster);
          }

          // Update app title
          if (config.appName) {
            document.title = `${config.appName} - Admin Panel`;
          }

          // Update favicon if provided
          if (config.faviconUrl) {
            const links = document.querySelectorAll("link[rel~='icon']");
            if (links.length > 0) {
              links.forEach(link => {
                link.href = config.faviconUrl;
              });
            } else {
              const newLink = document.createElement("link");
              newLink.rel = "icon";
              newLink.href = config.faviconUrl;
              document.getElementsByTagName("head")[0].appendChild(newLink);
            }
          }

          // Dynamic primary color theme styling injection
          // if (config.primaryColor) {
          //   document.documentElement.style.setProperty(
          //     "--primary-color",
          //     config.primaryColor
          //   );
          // }
        } else if (response.data?.success === false && response.data?.isActive !== false) {
          throw new Error("Invalid response structure or empty branding");
        }

        if (response.data && response.data.isActive === false) {
          setIsSuspended(true);
        }
      } catch (error) {
        if (error.response?.status === 403 || error.response?.data?.message?.includes("inactive") || error.response?.data?.isActive === false) {
          setIsSuspended(true);
          setLoading(false);
          return;
        }

        if (error.response?.status === 404) {
          setIsInvalidDomain(true);
          setLoading(false);
          return;
        }

        console.warn(
          "Failed to load dynamic branding configuration, using fallback default branding.",
          error
        );
        // Fallback default branding
        setBranding({
          appName: "ProTrade Platform",
          standardLogoUrl: "/images/logo.svg",
          whiteLogoUrl: "/images/logo-white.svg",
          faviconUrl: "/favicon.ico",
          primaryColor: "#0064FF",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  if (isSuspended) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", backgroundColor: "#f8f9fa", fontFamily: "sans-serif" }}>
        <h1 style={{ color: "#dc3545", marginBottom: "10px" }}>Account Suspended</h1>
        <p style={{ color: "#6c757d", fontSize: "16px" }}>This domain ecosystem is currently inactive. Please contact support.</p>
      </div>
    );
  }

  if (isInvalidDomain) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", backgroundColor: "#f8f9fa", fontFamily: "sans-serif" }}>
        <h1 style={{ color: "#dc3545", marginBottom: "10px" }}>Invalid Domain</h1>
        <p style={{ color: "#6c757d", fontSize: "16px" }}>This domain is not configured in the system. Please contact support.</p>
      </div>
    );
  }

  return (
    <BrandingContext.Provider value={{ branding, isMaster, loading }}>
      {!loading && children}
    </BrandingContext.Provider>
  );
};
